/* ==========================================================================
   PureSupps — Main JavaScript
   Vanilla JS · No frameworks · Production ready
   ========================================================================== */

(function () {
  "use strict";

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function showToast(message) {
    const toastEl = $("#ps-toast");
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.hidden = false;
    requestAnimationFrame(() => toastEl.classList.add("is-visible"));
    clearTimeout(showToast._timer);
    showToast._timer = setTimeout(() => {
      toastEl.classList.remove("is-visible");
      setTimeout(() => {
        toastEl.hidden = true;
      }, 350);
    }, 2600);
  }

  /* ------------------------------------------------------------------------
     1. Scroll Progress Bar
     ------------------------------------------------------------------------ */
  function initScrollProgress() {
    const bar = $("#scrollProgress");
    if (!bar) return;

    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      bar.style.width = progress + "%";
      bar.setAttribute("aria-valuenow", Math.round(progress));
    };

    window.addEventListener("scroll", update, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------------
     2. Sticky Header
     ------------------------------------------------------------------------ */
  function initHeader() {
    const header = $("#ps-header");
    const announcementEl = $(".ps-announcement");
    if (!header) return;

    const syncAnnouncement = () => {
      const hidden = !announcementEl || announcementEl.classList.contains("is-hidden");
      header.classList.toggle("is-flush", hidden);
      document.documentElement.style.setProperty("--announce-h", hidden ? "0px" : "36px");
    };

    const onScroll = () => {
      const threshold = 48;
      header.classList.toggle("is-solid", window.scrollY > threshold);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    syncAnnouncement();
    onScroll();

    const closeBtn = $("#ps-announcementClose");
    if (closeBtn && announcementEl) {
      closeBtn.addEventListener("click", () => {
        announcementEl.classList.add("is-hidden");
        syncAnnouncement();
        onScroll();
      });
    }
  }

  /* ------------------------------------------------------------------------
     3. Mobile Navigation
     ------------------------------------------------------------------------ */
  function initMobileNav() {
    const toggle = $("#navToggle");
    const nav = $("#mainNav");
    const overlay = $("#navOverlay");
    if (!toggle || !nav) return;

    const open = () => {
      nav.classList.add("is-open");
      toggle.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
      toggle.setAttribute("aria-label", "Close menu");
      if (overlay) overlay.hidden = false;
      document.body.style.overflow = "hidden";
    };

    const close = () => {
      nav.classList.remove("is-open");
      toggle.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Open menu");
      if (overlay) overlay.hidden = true;
      document.body.style.overflow = "";
      $$(".ps-nav-list__item--mega.is-open").forEach((el) => el.classList.remove("is-open"));
    };

    toggle.addEventListener("click", () => {
      nav.classList.contains("is-open") ? close() : open();
    });

    if (overlay) overlay.addEventListener("click", close);

    $$(".ps-nav-list__link", nav).forEach((link) => {
      link.addEventListener("click", (e) => {
        const parent = link.closest(".ps-nav-list__item--mega");
        if (parent && window.innerWidth <= 992) {
          e.preventDefault();
          parent.classList.toggle("is-open");
          link.setAttribute("aria-expanded", parent.classList.contains("is-open") ? "true" : "false");
          return;
        }
        close();
      });
    });

    $$(".ps-mega-menu__item", nav).forEach((item) => {
      item.addEventListener("click", close);
    });

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && nav.classList.contains("is-open")) close();
    });
  }

  /* ------------------------------------------------------------------------
     4. Search Modal
     ------------------------------------------------------------------------ */
  function initSearch() {
    const modal = $("#searchModal");
    const openBtn = $("#searchOpen");
    if (!modal || !openBtn) return;

    const open = () => {
      modal.hidden = false;
      document.body.style.overflow = "hidden";
      const input = $("#searchInput");
      if (input) setTimeout(() => input.focus(), 50);
    };

    const close = () => {
      modal.hidden = true;
      document.body.style.overflow = "";
      openBtn.focus();
    };

    openBtn.addEventListener("click", open);
    $$("[data-close-search]", modal).forEach((el) => el.addEventListener("click", close));

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal.hidden) close();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        open();
      }
    });
  }

  /* ------------------------------------------------------------------------
     5. Smooth Scroll (anchor links)
     ------------------------------------------------------------------------ */
  function initSmoothScroll() {
    $$('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", (e) => {
        const id = anchor.getAttribute("href");
        if (!id || id === "#") return;
        const target = $(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
      });
    });
  }

  /* ------------------------------------------------------------------------
     6. Scroll Reveal (Intersection Observer)
     ------------------------------------------------------------------------ */
  function initReveal() {
    const items = $$(".reveal");
    if (!items.length) return;

    if (prefersReducedMotion) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    items.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------------
     7. Animated Counters
     ------------------------------------------------------------------------ */
  function initCounters() {
    const counters = $$("[data-counter]");
    if (!counters.length) return;

    const animate = (el) => {
      const target = parseFloat(el.getAttribute("data-counter"));
      const decimals = parseInt(el.getAttribute("data-decimals") || "0", 10);
      const suffix = el.getAttribute("data-suffix") || "";
      const isK = suffix.startsWith("K");
      const duration = 1600;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        let value = target * eased;

        if (isK) {
          el.textContent = Math.round(value / 1000) + suffix;
        } else if (decimals > 0) {
          el.textContent = value.toFixed(decimals) + suffix;
        } else {
          el.textContent = Math.round(value) + suffix;
        }

        if (progress < 1) requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    };

    if (prefersReducedMotion) {
      counters.forEach((el) => {
        const target = parseFloat(el.getAttribute("data-counter"));
        const decimals = parseInt(el.getAttribute("data-decimals") || "0", 10);
        const suffix = el.getAttribute("data-suffix") || "";
        if (suffix.startsWith("K")) {
          el.textContent = Math.round(target / 1000) + suffix;
        } else if (decimals > 0) {
          el.textContent = target.toFixed(decimals) + suffix;
        } else {
          el.textContent = Math.round(target) + suffix;
        }
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------------
     8. Parallax
     ------------------------------------------------------------------------ */
  function initParallax() {
    if (prefersReducedMotion) return;
    const nodes = $$("[data-parallax]");
    if (!nodes.length) return;

    let ticking = false;

    const update = () => {
      const scrollY = window.scrollY;
      nodes.forEach((el) => {
        const speed = parseFloat(el.getAttribute("data-parallax")) || 0.2;
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + scrollY - scrollY) * speed * 0.15;
        const y = (window.innerHeight / 2 - rect.top) * speed * 0.08;
        el.style.transform = `translate3d(0, ${y + offset}px, 0)`;
      });
      ticking = false;
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------------
     9. Product Tabs (Featured)
     ------------------------------------------------------------------------ */
  function initTabs() {
    const tabSections = $$(".ps-section.ps-featured");
    if (!tabSections.length) return;

    tabSections.forEach((section) => {
      const buttons = section.querySelectorAll(".ps-tabs__btn");
      const cards = section.querySelectorAll(".ps-products-grid .ps-product-card");
      if (!buttons.length || !cards.length) return;

      buttons.forEach((btn) => {
        btn.addEventListener("click", () => {
          buttons.forEach((b) => {
            b.classList.remove("is-active");
            b.setAttribute("aria-selected", "false");
          });
          btn.classList.add("is-active");
          btn.setAttribute("aria-selected", "true");

          const filter = btn.getAttribute("data-tab");
          cards.forEach((card) => {
            const cat = card.getAttribute("data-category");
            const show = filter === "all" || cat === filter;
            card.classList.toggle("is-hidden", !show);
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     10. Best Sellers Slider
     ------------------------------------------------------------------------ */
  function initSlider() {
    const track = $("#bestSellerTrack");
    const prev = $("#bestPrev");
    const next = $("#bestNext");
    const dotsWrap = $("#bestSellerDots");
    if (!track) return;

    const slides = $$(".ps-product-card--slide", track);
    if (!slides.length) return;

    let index = 0;
    let perView = 4;
    let autoplayTimer = null;

    const getPerView = () => {
      const w = window.innerWidth;
      if (w <= 480) return 1;
      if (w <= 992) return 2;
      if (w <= 1200) return 3;
      return 4;
    };

    const maxIndex = () => Math.max(0, slides.length - perView);

    const renderDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = "";
      const pages = maxIndex() + 1;
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("aria-label", `Go to slide group ${i + 1}`);
        if (i === index) dot.classList.add("is-active");
        dot.addEventListener("click", () => goTo(i));
        dotsWrap.appendChild(dot);
      }
    };

    const update = () => {
      const slide = slides[0];
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.gap) || 24;
      const width = slide.getBoundingClientRect().width;
      track.style.transform = `translateX(-${index * (width + gap)}px)`;
      if (dotsWrap) {
        $$("button", dotsWrap).forEach((d, i) => d.classList.toggle("is-active", i === index));
      }
    };

    const goTo = (i) => {
      index = Math.max(0, Math.min(i, maxIndex()));
      update();
    };

    const nextSlide = () => {
      index = index >= maxIndex() ? 0 : index + 1;
      update();
      renderDots();
      $$("button", dotsWrap).forEach((d, i) => d.classList.toggle("is-active", i === index));
    };

    const prevSlide = () => {
      index = index <= 0 ? maxIndex() : index - 1;
      update();
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (prefersReducedMotion) return;
      autoplayTimer = setInterval(nextSlide, 4500);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) clearInterval(autoplayTimer);
    };

    if (prev) prev.addEventListener("click", () => { prevSlide(); startAutoplay(); });
    if (next) next.addEventListener("click", () => { nextSlide(); startAutoplay(); });

    const slider = $("#bestSellerSlider");
    if (slider) {
      slider.addEventListener("mouseenter", stopAutoplay);
      slider.addEventListener("mouseleave", startAutoplay);
      slider.addEventListener("focusin", stopAutoplay);
      slider.addEventListener("focusout", startAutoplay);
    }

    const onResize = () => {
      perView = getPerView();
      if (index > maxIndex()) index = maxIndex();
      renderDots();
      update();
    };

    window.addEventListener("resize", onResize);
    onResize();
    startAutoplay();
  }

  /* ------------------------------------------------------------------------
     11. Wishlist / Cart / Quick View
     ------------------------------------------------------------------------ */
  function initCommerce() {
    let cartCount = parseInt(($("#cartCount") || {}).textContent || "2", 10);
    let wishCount = parseInt(($("#wishlistCount") || {}).textContent || "3", 10);

    $$(".ps-add-cart-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        cartCount += 1;
        const badge = $("#cartCount");
        if (badge) badge.textContent = String(cartCount);
        showToast("Added to cart");
      });
    });

    $$(".ps-product-card__wish").forEach((btn) => {
      btn.addEventListener("click", () => {
        const active = btn.classList.toggle("is-active");
        wishCount += active ? 1 : -1;
        wishCount = Math.max(0, wishCount);
        const badge = $("#wishlistCount");
        if (badge) badge.textContent = String(wishCount);
        showToast(active ? "Added to wishlist" : "Removed from wishlist");
      });
    });

    const qvModal = $("#qvModal");
    const qvTitle = $("#qvTitle");

    $$(".quick-view-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!qvModal) return;
        if (qvTitle) qvTitle.textContent = btn.getAttribute("data-product") || "Product";
        qvModal.hidden = false;
        document.body.style.overflow = "hidden";
      });
    });

    if (qvModal) {
      const close = () => {
        qvModal.hidden = true;
        document.body.style.overflow = "";
      };
      $$("[data-close-qv]", qvModal).forEach((el) => el.addEventListener("click", close));
      window.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !qvModal.hidden) close();
      });
    }

    const cartOpen = $("#cartOpen");
    if (cartOpen) {
      cartOpen.addEventListener("click", () => showToast(`Cart · ${cartCount} item${cartCount === 1 ? "" : "s"}`));
    }
  }

  /* ------------------------------------------------------------------------
     12. Button Ripple
     ------------------------------------------------------------------------ */
  function initRipple() {
    $$(".btn--ripple").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const rect = btn.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const ripple = document.createElement("span");
        ripple.className = "ripple";
        ripple.style.width = ripple.style.height = size + "px";
        ripple.style.left = e.clientX - rect.left - size / 2 + "px";
        ripple.style.top = e.clientY - rect.top - size / 2 + "px";
        btn.appendChild(ripple);
        setTimeout(() => ripple.remove(), 650);
      });
    });
  }

  /* ------------------------------------------------------------------------
     13. Newsletter
     ------------------------------------------------------------------------ */
  function initNewsletter() {
    const form = $("#ps-newsletterForm");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      const input = $("#ps-newsletterEmail");
      const msg = $("#ps-newsletterMsg");
      const noteText = form.dataset.noteText || "";
      const submitBtn = form.querySelector('button[type="submit"]');
      const email = (input && input.value || "").trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      if (msg) {
        msg.textContent = "";
        msg.classList.remove("is-error", "is-success");
      }

      if (!valid) {
        if (msg) {
          msg.textContent = "Please enter a valid email address.";
          msg.classList.add("is-error");
        }
        if (input) input.focus();
        return false;
      }

      if (submitBtn) submitBtn.disabled = true;

      const action = form.action || '/contact';

      const body = new URLSearchParams(new FormData(form));

      const submitToIframe = () => {
        try {
          let iframe = document.getElementById('ps-newsletter-iframe');
          if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.name = 'ps-newsletter-iframe';
            iframe.id = 'ps-newsletter-iframe';
            document.body.appendChild(iframe);
          }
          form.target = iframe.name;
          form.submit();
          if (msg) {
            msg.textContent = noteText || "You're successfully subscribed to our newsletter.";
            msg.classList.add('is-success');
          }
          form.reset();
          showToast('Subscribed successfully');
        } catch (err) {
          console.error('Iframe fallback failed', err);
          if (msg) {
            msg.textContent = 'Subscription failed — please try again later.';
            msg.classList.add('is-error');
          }
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      };

      try {
        const response = await fetch(action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body,
          redirect: 'follow'
        });

        // Helpful debug logging for server response
        let respText = "";
        try { respText = await response.text(); } catch (e) { /* ignore */ }
        console.debug('Newsletter POST', { action, status: response.status, ok: response.ok, redirected: response.redirected, bodyPreview: respText.slice(0, 200) });

        // Treat 2xx and 3xx as success (some setups redirect)
        if (response.status >= 200 && response.status < 400) {
          if (msg) {
            msg.textContent = noteText || "You're successfully subscribed to our newsletter.";
            msg.classList.add("is-success");
          }
          form.reset();
          showToast("Subscribed successfully");
        } else {
          console.warn('Newsletter POST returned non-2xx, trying FormData fallback', { status: response.status, body: respText });
          // Some setups reject URL-encoded fetch; try submitting as FormData without Content-Type header
          try {
            const fd = new FormData(form);
            const second = await fetch(action, { method: 'POST', credentials: 'same-origin', body: fd, redirect: 'follow' });
            const secondText = await (second.text().catch(() => ''));
            console.debug('Newsletter FormData POST', { status: second.status, ok: second.ok, bodyPreview: secondText.slice(0, 200) });
            if (second.status >= 200 && second.status < 400) {
              if (msg) {
                msg.textContent = noteText || "You're successfully subscribed to our newsletter.";
                msg.classList.add('is-success');
              }
              form.reset();
              showToast('Subscribed successfully');
            } else {
              console.warn('FormData fallback failed, falling back to iframe', { status: second.status, body: secondText });
              submitToIframe();
            }
          } catch (err) {
            console.error('FormData fallback error', err);
            submitToIframe();
          }
        }
      } catch (error) {
        console.error('Newsletter subscribe error, trying iframe fallback', error);
        submitToIframe();
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }

      return false;
    });

    const footerForm = $(".ps-footer__mini-form");
    if (footerForm) {
      footerForm.addEventListener("submit", (e) => {
        e.preventDefault();
        showToast("Thanks for subscribing");
        footerForm.reset();
      });
    }
  }

  /* ------------------------------------------------------------------------
     14. Accordion (Footer mobile)
     ------------------------------------------------------------------------ */
  function initAccordion() {
    $$(".accordion__trigger").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        if (window.innerWidth > 992) return;
        const expanded = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", String(!expanded));
        const panelId = trigger.getAttribute("aria-controls");
        const panel = panelId ? $("#" + panelId) : trigger.closest(".ps-footer__col")?.querySelector(".accordion__panel");
        if (panel) panel.classList.toggle("is-open", !expanded);
      });
    });
  }

  /* ------------------------------------------------------------------------
     15. Back to Top
     ------------------------------------------------------------------------ */
  function initBackTop() {
    const btn = $("#backTop");
    if (!btn) return;

    const toggle = () => {
      btn.classList.toggle("is-visible", window.scrollY > 600);
    };

    window.addEventListener("scroll", toggle, { passive: true });
    toggle();

    btn.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    });
  }

  /* ------------------------------------------------------------------------
     16. Lazy Loading Enhancement
     ------------------------------------------------------------------------ */
  function initLazyLoading() {
    if ("loading" in HTMLImageElement.prototype) return;
    $$("img[loading='lazy']").forEach((img) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            if (el.dataset.src) el.src = el.dataset.src;
            observer.unobserve(el);
          }
        });
      });
      observer.observe(img);
    });
  }

  /* ------------------------------------------------------------------------
     17. Mouse Follow Glow (PS Hero)
     ------------------------------------------------------------------------ */
  function initPsHeroMouseFollow() {
    if (prefersReducedMotion || window.innerWidth < 992) return;
    const el = $("#ps-hero");
    if (!el) return;

    el.addEventListener("mousemove", (e) => {
      const glow = $(".swiper-slide-active .ps-hero__glow", el) || $(".ps-hero__glow", el);
      if (!glow) return;
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 40;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 40;
      glow.style.transform = `translate(${x}px, ${y}px)`;
    });
  }

  /* ------------------------------------------------------------------------
     18. PS Hero Swiper
     ------------------------------------------------------------------------ */
  function initPsHeroSwiper() {
    const el = $("#ps-hero");
    if (!el || typeof Swiper === "undefined") return;

    const swiperContainer = el.querySelector('.ps-hero-swiper') || el.querySelector('.swiper');
    if (!swiperContainer) return;

    const slideCount = (swiperContainer.querySelectorAll('.swiper-slide') || []).length;
    const enableLoop = slideCount > 1;

    function restartPsHeroAnimation() {
      const active = swiperContainer.querySelector('.swiper-slide-active');
      if (!active) return;
      active.querySelectorAll('.ps-hero__copy, .ps-hero__visual').forEach((node) => {
        node.style.animation = 'none';
        void node.offsetWidth;
        node.style.animation = '';
      });
    }

    function syncPsHeroNavAlignment() {
      // lightweight safe alignment: no-op if required elements missing
      const nav = el.querySelector('.ps-hero__nav');
      if (!nav) return;
      // keep minimal alignment to avoid layout errors
      nav.style.bottom = '';
    }

    const swiper = new Swiper(swiperContainer, {
      loop: enableLoop,
      speed: 850,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      autoHeight: false,
      watchOverflow: true,
      grabCursor: true,
      autoplay: prefersReducedMotion || slideCount < 2 ? false : { delay: 5500, disableOnInteraction: false, pauseOnMouseEnter: true },
      pagination: { el: el.querySelector('.ps-hero-swiper__pagination') || '.ps-hero-swiper__pagination', clickable: true },
      navigation: { nextEl: el.querySelector('.ps-hero-swiper__next') || '.ps-hero-swiper__next', prevEl: el.querySelector('.ps-hero-swiper__prev') || '.ps-hero-swiper__prev' },
      a11y: { prevSlideMessage: 'Previous product slide', nextSlideMessage: 'Next product slide' },
      on: {
        init() { requestAnimationFrame(() => { syncPsHeroNavAlignment(); restartPsHeroAnimation(); }); },
        slideChangeTransitionStart() { restartPsHeroAnimation(); },
        slideChangeTransitionEnd() { syncPsHeroNavAlignment(); },
        resize() { syncPsHeroNavAlignment(); }
      }
    });

    window.addEventListener('resize', syncPsHeroNavAlignment, { passive: true });
    window.addEventListener('load', syncPsHeroNavAlignment, { passive: true });

    return swiper;
  }

  /* ------------------------------------------------------------------------
     19. Categories Swiper
     ------------------------------------------------------------------------ */
  function initCategoriesSwiper() {
    const el = $(".ps-categories-swiper");
    if (!el || typeof Swiper === "undefined") return;

    new Swiper(el, {
      slidesPerView: 2.2,
      spaceBetween: 16,
      grabCursor: true,
      pagination: {
        el: ".ps-categories-swiper__pagination",
        clickable: true,
      },
      breakpoints: {
        480: { slidesPerView: 3, spaceBetween: 18 },
        768: { slidesPerView: 4, spaceBetween: 20 },
        992: { slidesPerView: 5, spaceBetween: 22 },
        1200: { slidesPerView: 7, spaceBetween: 20 },
      },
    });
  }

  /* ------------------------------------------------------------------------
     20. Testimonials Swiper
     ------------------------------------------------------------------------ */
  function initTestimonialsSwiper() {
    const el = $(".ps-testimonials-swiper");
    if (!el || typeof Swiper === "undefined") return;

    new Swiper(el, {
      slidesPerView: 1.15,
      spaceBetween: 16,
      grabCursor: true,
      loop: true,
      speed: 650,
      autoplay: prefersReducedMotion
        ? false
        : {
            delay: 4500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },
      pagination: {
        el: ".ps-testimonials-swiper__pagination",
        clickable: true,
      },
      navigation: {
        nextEl: ".ps-testimonials__next",
        prevEl: ".ps-testimonials__prev",
      },
      breakpoints: {
        640: { slidesPerView: 1.6, spaceBetween: 18 },
        768: { slidesPerView: 2.2, spaceBetween: 20 },
        992: { slidesPerView: 2.6, spaceBetween: 22 },
        1200: { slidesPerView: 3, spaceBetween: 24 },
      },
      a11y: {
        prevSlideMessage: "Previous review",
        nextSlideMessage: "Next review",
      },
    });
  }

    document.addEventListener("DOMContentLoaded", function () {
      var copyBtn = document.getElementById("promiseCopyBtn");
      if (!copyBtn) return;

      var defaultText = copyBtn.textContent;

      copyBtn.addEventListener("click", function () {
        var code = copyBtn.getAttribute("data-copy-code") || "POWERUP";

        function resetButton() {
          copyBtn.textContent = "Copied";
          copyBtn.disabled = true;
          window.setTimeout(function () {
            copyBtn.textContent = defaultText;
            copyBtn.disabled = false;
          }, 1400);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(resetButton).catch(function () {
            var hiddenInput = document.createElement("textarea");
            hiddenInput.value = code;
            hiddenInput.setAttribute("readonly", "");
            hiddenInput.style.position = "absolute";
            hiddenInput.style.left = "-9999px";
            document.body.appendChild(hiddenInput);
            hiddenInput.select();
            document.execCommand("copy");
            document.body.removeChild(hiddenInput);
            resetButton();
          });
          return;
        }

        var hiddenInput = document.createElement("textarea");
        hiddenInput.value = code;
        hiddenInput.setAttribute("readonly", "");
        hiddenInput.style.position = "absolute";
        hiddenInput.style.left = "-9999px";
        document.body.appendChild(hiddenInput);
        hiddenInput.select();
        document.execCommand("copy");
        document.body.removeChild(hiddenInput);
        resetButton();
      });
    });

    document.addEventListener("pointerdown", function (event) {
  const reviewBadge = event.target.closest(".jdgm-prev-badge");

  if (!reviewBadge) return;

  const reviewSection = document.getElementById("judgeme_product_reviews");

  if (!reviewSection) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const header = document.querySelector(
    "header, .header, .shopify-section-header"
  );

  const headerHeight = header
    ? header.getBoundingClientRect().height
    : 0;

  const sectionTop =
    reviewSection.getBoundingClientRect().top +
    window.scrollY -
    headerHeight -
    20;

  window.scrollTo({
    top: sectionTop,
    behavior: "smooth"
  });
}, true);

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */
  document.addEventListener("DOMContentLoaded", () => {
    initScrollProgress();
    initHeader();
    initMobileNav();
    initSearch();
    initSmoothScroll();
    initReveal();
    initCounters();
    initParallax();
    initTabs();
    initSlider();
    initCommerce();
    initRipple();
    initNewsletter();
    initAccordion();
    initBackTop();
    initLazyLoading();
    initPsHeroMouseFollow();
    initPsHeroSwiper();
    initCategoriesSwiper();
    initTestimonialsSwiper();
  });
})();
